import { enqueueSnackbar } from "notistack";
import {
  getAllCatalogueChildrenbyParentCode,
  getAllClientsNoPagination,
  getAllEquipmentStatuses,
  getAllEquipments,
  getAllEquipmentsByClientId,
  getAllNoPaginationEquipmentsByClientIdByTypeEquipmentId,
  getAllOts,
  getAllReports,
} from "../../../../libs/apis";
import {
  CatalogueI,
  ClientI,
  EquipmentI,
  EquipmentSimpleI,
  OptionI,
  OtI,
  ParameterI,
  ReportI,
  SupplierI,
} from "../../../../libs/interfaces";
import { useEffect, useMemo, useState } from "react";
import { useRouterStore } from "../../../../libs/stores";
import { CATALOG_EQUIPMENTS_CODE } from "../../../../libs/constants";
import { useDashboardStore } from "../store/dashboard.store";

const icons = {
  EQUIPMENT_AC: "❄️",
  EQUIPMENT_BOARD_ELECTRICAL: "⚡",
  EQUIPMENT_CAMS: "📹",
  EQUIPMENT_BOMBAS: "💧",
  EQUIPMENT_COMPUTER: "💻",
  EQUIPMENT_DOMOTICA: "🤖",
  EQUIPMENT_GENERIC: "💻",
  EQUIPMENT_VEHICLE: "🚗",
  EQUIPMENT_MULTIMEDIA: "🎵",
  EQUIPMENT_SECURITY: "🔒",
  EQUIPMENT_GENERATORS: "⚡",
  EQUIPMENT_SOLAR: "☀️",
  EQUIPMENT_KITCHEN_LAUNDRY: "🍳",
  EQUIPMENT_BOMBA: "💧",
  EQUIPMENT_UPS: "🔋",
} as any;

const useHookDashboard = () => {
  const clientId = useRouterStore((state) => state.clientId);
  const clientSelected = useDashboardStore((state) => state.clientSelected);
  const setClientSelected = useDashboardStore(
    (state) => state.setClientSelected
  );
  const equipments = useDashboardStore((state) => state.equipments);
  const typeEquipment = useDashboardStore((state) => state.typeEquipment);
  const setTypeEquipment = useDashboardStore((state) => state.setTypeEquipment);
  const setEquipments = useDashboardStore((state) => state.setEquipments);

  const [clients, setClients] = useState<ClientI[]>([]);

  const [typeEquipments, setTypeEquipments] = useState<CatalogueI[]>([]);
  const [statusEquipments, setStatusEquipments] = useState<ParameterI[]>([]);
  const [ots, setOts] = useState<OtI[]>([]);
  const [reports, setReports] = useState<ReportI[]>([]);

  const profile = useRouterStore((state) => state.profile);
  const firstName = useRouterStore((state) => state.firstName);

  const isAdmin = profile === "Administrador";

  useEffect(() => {
    handleGetData();
  }, []);

  const handleGetData = async () => {
    try {
      setTypeEquipment(null);
      const { data: dataClients } = await getAllClientsNoPagination();
      if (!dataClients) return;
      setClients(dataClients);
      const { data: dataCatalogues } =
        await getAllCatalogueChildrenbyParentCode(CATALOG_EQUIPMENTS_CODE);
      if (!dataCatalogues) return;
      setTypeEquipments(dataCatalogues);

      const { data: dataStatuses } = await getAllEquipmentStatuses();
      if (!dataStatuses) return;
      setStatusEquipments(dataStatuses);

      if (clientId || clientSelected) {
        const { data: dataEquipments } = await getAllEquipmentsByClientId(
          clientId || clientSelected!
        );
        if (!dataEquipments) return;
        setEquipments(dataEquipments);
        if (!clientSelected) setClientSelected(clientId);
      } else {
        const { data: dataEquipments } = await getAllEquipments();
        if (!dataEquipments) return;
        setEquipments(dataEquipments);
      }

      await getOts(clientId || clientSelected!);
      await getReports(clientId || clientSelected!);
    } catch (err: any) {
      console.log(err);
      enqueueSnackbar(err.toString(), { variant: "error" });
    }
  };

  const handleSelectClient = async (client: number | null) => {
    try {
      if (!client) {
        const { data: dataEquipments } =
          await getAllNoPaginationEquipmentsByClientIdByTypeEquipmentId(
            null,
            typeEquipment
          );
        if (!dataEquipments) return;
        setEquipments(dataEquipments);
        setClientSelected(null);
        await getOts(null);
        await getReports(null);
        return;
      }
      const { data: dataEquipments } =
        await getAllNoPaginationEquipmentsByClientIdByTypeEquipmentId(
          client!,
          typeEquipment
        );
      if (!dataEquipments) return;
      setEquipments(dataEquipments);
      setClientSelected(client);
      await getOts(client);
      await getReports(client);
    } catch (err: any) {
      console.log(err);
      enqueueSnackbar(err.toString(), { variant: "error" });
    }
  };

  const handleSelectTypeEquipment = async (type: number | null) => {
    try {
      if (!type) {
        if (clientId || clientSelected) {
          const { data: dataEquipments } =
            await getAllNoPaginationEquipmentsByClientIdByTypeEquipmentId(
              clientId || clientSelected!,
              typeEquipment
            );
          if (!dataEquipments) return;
          setEquipments(dataEquipments);
        } else {
          const { data: dataEquipments } = await getAllEquipments();
          if (!dataEquipments) return;
          setEquipments(dataEquipments);
        }
        setTypeEquipment(null);
        return;
      }
      setTypeEquipment(type);

      const { data: dataEquipments } =
        await getAllNoPaginationEquipmentsByClientIdByTypeEquipmentId(
          clientId || clientSelected!,
          type
        );
      if (!dataEquipments) return;
      setEquipments(dataEquipments);
    } catch (err) {
      console.log(err);
    }
  };

  const getOts = async (client?: number | null) => {
    try {
      const { data: dataOts } = await getAllOts(client);
      if (!dataOts) return;
      setOts(dataOts);
    } catch (err: any) {
      console.log(err);
      enqueueSnackbar(err.toString(), { variant: "error" });
    }
  };

  const getReports = async (client?: number | null) => {
    try {
      const { data: dataReports } = await getAllReports(client);
      if (!dataReports) return;
      setReports(dataReports);
    } catch (err: any) {
      console.log(err);
      enqueueSnackbar(err.toString(), { variant: "error" });
    }
  };

  const assetsByType = useMemo(() => {
    return equipments.reduce((acc, equipment) => {
      if (!acc.find((el: any) => el.id === equipment.typeEquipmentId)) {
        acc.push({
          id: equipment.typeEquipmentId,
          name: equipment.typeEquipment,
          code: equipment.typeEquipmentCode!,
          count: 1,
          icon: icons[equipment.typeEquipmentCode!],
        });
      } else {
        const index = acc.findIndex(
          (el: any) => el.id === equipment.typeEquipmentId
        );
        acc[index].count++;
      }
      return acc;
    }, [] as any);
  }, [equipments, clientSelected]);

  // Nueva función para procesar OTs por tipo de equipo para el PieChart
  const otsByEquipmentType = useMemo(() => {
    const typeMap: { [key: number]: any } = {};

    ots.forEach((ot: OtI) => {
      const typeId = ot.typeEquipmentId;

      if (!typeMap[typeId!]) {
        typeMap[typeId!] = {
          id: typeId,
          name: ot.equipment?.split(" ").slice(0, 2).join(" ") || "Otros",
          code: ot.equipmentCode || "",
          count: 0,
          icon: icons[(ot.equipment as any)?.split(" ")[0]] || "📦",
        };
      }
      typeMap[typeId!].count++;
    });

    return Object.values(typeMap);
  }, [ots, clientSelected]);

  const recentActivities = useMemo(() => {
    // Ordenar reportes por fecha más reciente primero
    const sortedReports = [...reports].sort((a, b) => {
      const dateA = new Date(`${a.workDate} ${a.hour || "00:00"}`);
      const dateB = new Date(`${b.workDate} ${b.hour || "00:00"}`);
      return dateB.getTime() - dateA.getTime();
    });

    // Tomar solo los últimos 10 reportes
    return sortedReports.slice(0, 10).map((report) => {
      // Determinar el tipo de actividad basado en el trabajo realizado
      let activityType = "maintenance";
      let icon = "🔧";

      if (report.typeWork?.toLowerCase().includes("preventivo")) {
        activityType = "preventive";
        icon = "📋";
      } else if (report.typeWork?.toLowerCase().includes("correctivo")) {
        activityType = "corrective";
        icon = "🔨";
      } else if (report.typeWork?.toLowerCase().includes("emergencia")) {
        activityType = "emergency";
        icon = "🚨";
      } else if (report.typeWork?.toLowerCase().includes("reparación")) {
        activityType = "repair";
        icon = "⚙️";
      }

      // Calcular tiempo transcurrido
      const activityDate = new Date(
        `${report.workDate} ${report.hour || "00:00"}`
      );
      const now = new Date();
      const diffTime = Math.abs(now.getTime() - activityDate.getTime());
      const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
      const diffDays = Math.floor(diffHours / 24);

      let timeAgo = "";
      if (diffDays > 0) {
        timeAgo = `Hace ${diffDays} día${diffDays > 1 ? "s" : ""}`;
      } else if (diffHours > 0) {
        timeAgo = `Hace ${diffHours} hora${diffHours > 1 ? "s" : ""}`;
      } else {
        timeAgo = "Hace unos minutos";
      }

      return {
        id: report.id,
        type: activityType,
        icon: icon,
        title: report.typeWork || "Trabajo realizado",
        description: report.workDone || report.observation || "Sin descripción",
        equipment: report.equipment || "Equipo no especificado",
        location: report.clientAddress || report.address || "Sin ubicación",
        technician:
          report.workPerson || report.supplierContact || "Sin técnico",
        time: timeAgo,
        date: report.workDate,
        status: report.statusOt || "Sin estado",
        invoice: report.invoice,
        numberInvoice: report.numberInvoice,
        priceInvoice: report.priceInvoice,
        initialState: report.initialState,
        finalState: report.finalState,
      };
    });
  }, [reports, clientSelected]);

  return {
    clients,
    profile,
    isAdmin,
    typeEquipments,
    statusEquipments,
    equipments,
    clientSelected,
    setClientSelected,
    handleSelectClient,
    handleSelectTypeEquipment,
    typeEquipment,
    firstName,
    assetsByType,
    ots, 
    otsByEquipmentType, 
    recentActivities
  };
};

export default useHookDashboard;
